import React, { useState, useEffect, useMemo } from "react";
import {
  DataTable,
  Button,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { FileText } from "lucide-react";
import { getBackfillOperations, BackfillOperation } from "../../api";

const formatColumnHeader = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (value: string | null | undefined) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
};

const LogsTable: React.FC<{ onShowLogs: (id: number) => void }> = ({
  onShowLogs,
}) => {
  const [operations, setOperations] = useState<BackfillOperation[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOperations = async () => {
      setLoading(true);
      try {
        const skip = page * rowsPerPage;
        const limit = rowsPerPage;
        const { items, total_count } = await getBackfillOperations(skip, limit);
        setOperations(items);
        setTotalCount(total_count);
      } catch (err) {
        console.error("Error fetching backfill operations", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOperations();
  }, [page, rowsPerPage]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setRowsPerPage(newSize);
    setPage(0);
  };

  const getStatusBadge = (status: string) => {
    const variant =
      status === "Completed"
        ? "default"
        : status === "Error"
          ? "destructive"
          : "outline";
    const className =
      status === "Running"
        ? "bg-amber-100 text-amber-800 border-amber-300"
        : status === "Completed"
          ? "bg-green-100 text-green-800 border-green-300"
          : status === "Error"
            ? "bg-red-100 text-red-800 border-red-300"
            : "";
    return (
      <Badge variant={variant} className={className}>
        {status}
      </Badge>
    );
  };

  const dataTableColumns = useMemo((): TableColumn<BackfillOperation>[] => {
    const dataKeys: (keyof BackfillOperation)[] = [
      "start_time",
      "end_time",
      "job_count",
      "failed_job_count",
      "created_at",
      "updated_at",
    ];
    const cols: TableColumn<BackfillOperation>[] = dataKeys.map((key) => ({
      key,
      header: formatColumnHeader(key),
      accessor: (row) => row[key],
      width: key === "start_time" || key === "end_time" ? "14%" : "12%",
      render: (value) =>
        key.includes("_time") || key.includes("_at")
          ? formatDate(value as string)
          : String(value ?? ""),
    }));
    cols.push({
      key: "status",
      header: "Status",
      accessor: (row) => row.status,
      width: "12%",
      align: "center",
      render: (_v, row) => (
        <div className="flex justify-center">{getStatusBadge(row.status)}</div>
      ),
    });
    cols.push({
      key: "actions",
      header: "Show Logs",
      accessor: (row) => row.id,
      width: "100px",
      align: "center",
      render: (_v, row) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onShowLogs(row.id)}
                className="h-8 w-8 p-0 [&_svg]:text-black [&_svg]:hover:text-black"
              >
                <FileText className="h-4 w-4 text-black" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>View jobs</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    });
    return cols;
  }, [onShowLogs]);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  return (
    <div className="flex flex-col h-full min-h-0">
      <DataTable<BackfillOperation>
        className="flex-1 min-h-0 overflow-auto"
        maxHeight="calc(100vh - 170px)"
        columns={dataTableColumns}
        data={operations}
        loading={loading}
        emptyMessage="No operations found."
        stickyHeader
        stickyFooter
        showActions={false}
        pagination={{
          currentPage: page + 1,
          totalPages,
          pageSize: rowsPerPage,
          totalItems: totalCount,
          onPageChange: (p) => handlePageChange(p - 1),
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [25, 50, 100],
        }}
      />
    </div>
  );
};

export default LogsTable;
